<template>
    <div>
        <breadcrumb :items="breadcrumbs" />
        <v-container fluid>
            <div class="d-flex justify-space-between align-center flex-wrap mb-6">
                <h2 class="page-title">{{ $t('surveyBuilder.surveys') }}</h2>
            </div>

            <v-card flat class="pa-4">
                <v-row class="mb-4" align="center">
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="search"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :placeholder="$t('surveyBuilder.searchAssessments')"
                            clearable
                        />
                    </v-col>
                </v-row>

                <v-alert v-if="error" type="error" outlined class="mb-4">
                    {{ error }}
                </v-alert>

                <v-data-table
                    :headers="headers"
                    :items="filteredAssessments"
                    :loading="loading"
                    :loading-text="$t('surveyBuilder.loadingSurveys')"
                    :no-data-text="$t('surveyBuilder.noSurveysAssigned')"
                    class="cc-assessment-table"
                    hide-default-footer
                    :items-per-page="-1"
                >
                    <template #[`item.title`]="{ item }">
                        <span class="font-weight-medium">{{ item.title }}</span>
                    </template>

                    <template #[`item.description`]="{ item }">
                        <span class="text-truncate description-cell">{{ stripHtml(item.description) }}</span>
                    </template>

                    <template #[`item.noOfQuestions`]="{ item }">
                        {{ item.noOfQuestions || '-' }}
                    </template>

                    <template #[`item.respondentType`]="{ item }">
                        <v-chip small outlined>{{ item.respondentType }}</v-chip>
                    </template>

                    <template #[`item.actions`]="{ item }">
                        <v-btn icon small color="#00BD73" @click="viewAssessment(item)">
                            <v-icon size="20">mdi-eye-outline</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import SurveyService from '@/_services/SurveyService';

export default {
    name: 'SurveyList',
    mixins: [loadingMixin],

    data() {
        return {
            assessments: [],
            search: '',
            loading: false,
            error: null,
        };
    },

    computed: {
        breadcrumbs() {
            return [
                { text: this.$t('surveyBuilder.surveys'), disabled: true },
            ];
        },

        headers() {
            return [
                { text: this.$t('surveyBuilder.surveyTitle'), value: 'title', sortable: true },
                { text: this.$t('surveyBuilder.description'), value: 'description', sortable: false },
                { text: this.$t('surveyBuilder.questions'), value: 'noOfQuestions', sortable: true, width: '150px', align: 'center' },
                { text: this.$t('surveyBuilder.audience'), value: 'respondentType', sortable: true },
                { text: this.$t('surveyBuilder.actions'), value: 'actions', sortable: false, width: '80px', align: 'center' },
            ];
        },

        filteredAssessments() {
            if (!this.search) return this.assessments;
            const term = this.search.toLowerCase();
            return this.assessments.filter(a =>
                (a.title || '').toLowerCase().includes(term) ||
                (a.description || '').toLowerCase().includes(term)
            );
        },
    },

    async mounted() {
        await this.fetchAssessments();
    },

    methods: {
        async fetchAssessments() {
            this.loading = true;
            this.error = null;
            try {
                const response = await SurveyService.getAssignedSurveys({
                    respondentType: 'Sub-Organization',
                    attachedModule: 'Survey'
                });
                this.assessments = response?.data || [];
            } catch (err) {
                this.error = this.$t('surveyBuilder.failedToLoadSurveys');
                console.error(err);
            } finally {
                this.loading = false;
            }
        },

        viewAssessment(assessment) {
            this.$router.push({
                name: 'surveyQuestions',
                params: { assessmentId: assessment._id },
                query: { respondentType: assessment.respondentType },
            });
        },

        stripHtml(html) {
            if (!html) return '-';
            const div = document.createElement('div');
            div.innerHTML = html;
            return div.textContent || div.innerText || '-';
        },
    },
};
</script>

<style lang="scss" scoped>
.page-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
}

.description-cell {
    display: inline-block;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cc-assessment-table {
    border: 1px solid #e4e8ef;
    border-radius: 8px;
}
</style>
